import { useEffect, useRef, useState } from "react";
import type { StreamPlayerProps } from "../types";
import { useInputCapture } from "../hooks/useInputCapture";
import StatusOverlay from "./StatusOverlay";

/**
 * WebRTC stream player component.
 *
 * Renders a <video> element that displays the remote MediaStream.
 * Attaches the stream to the video element via ref when the stream
 * prop changes. Captures input events when onInputEvent is provided.
 * Includes a StatusOverlay for connection state and stats.
 */
export default function StreamPlayer({
  stream,
  connectionState,
  stats,
  showStats = true,
  onInputEvent,
  vmResolution,
  onVideoRef,
  pointerLockEnabled = false,
  onPointerLockChange,
  resolutionChanging = false,
  videoTimeoutSec = 30,
  thumbnailUrl,
  vmWaiting = false,
}: StreamPlayerProps & {
  onVideoRef?: (el: HTMLVideoElement | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [waitingElapsed, setWaitingElapsed] = useState(0);
  const [videoTimedOut, setVideoTimedOut] = useState(false);

  // Capture mouse/keyboard/wheel events on the video element
  useInputCapture(videoRef, {
    sendFn: onInputEvent ?? (() => {}),
    vmResolution,
    enabled: !!onInputEvent && connectionState === "connected",
    pointerLockEnabled,
    onPointerLockChange,
  });

  // Expose video element to parent
  useEffect(() => {
    onVideoRef?.(videoRef.current);
    return () => onVideoRef?.(null);
  }, [onVideoRef]);

  // Start the deadline with negotiation: an SDP track can arrive while ICE
  // still fails. Neither that track nor ICE success restarts the frame budget.
  const isWaiting = connectionState === "connecting"
    || (connectionState === "connected" && !videoReady);
  const transportInterrupted = connectionState === "disconnected" && !!stream;
  useEffect(() => {
    if (!isWaiting) {
      setWaitingElapsed(0);
      setVideoTimedOut(false);
      return;
    }
    const start = Date.now();
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - start) / 1000);
      setWaitingElapsed(elapsed);
      if (elapsed >= videoTimeoutSec) {
        setVideoTimedOut(true);
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [isWaiting, videoTimeoutSec]);

  // Attach/detach MediaStream to the video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (stream) {
      video.srcObject = stream;
      // Only reset videoReady if video isn't already showing frames
      // (avoids flicker when stream reference changes due to new audio track)
      if (video.readyState < 2) {
        setVideoReady(false);
      }
      const markReady = () => setVideoReady(true);
      video.addEventListener("playing", markReady);
      video.addEventListener("loadeddata", markReady);
      // Already playing (re-render case)
      if (video.readyState >= 3) markReady();
      return () => {
        video.removeEventListener("playing", markReady);
        video.removeEventListener("loadeddata", markReady);
        video.srcObject = null;
      };
    } else {
      video.srcObject = null;
      setVideoReady(false);
    }
  }, [stream]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Always start muted; SessionView controls video.muted via ref */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />

      {/* Thumbnail placeholder while video loads */}
      {thumbnailUrl && !videoReady && (
        <img
          src={thumbnailUrl}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            zIndex: 0,
          }}
        />
      )}

      {/* Disconnected placeholder */}
      {connectionState === "disconnected" && !stream && !thumbnailUrl && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#666",
            gap: 12,
          }}
        >
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
          </svg>
          <span style={{ fontSize: 14 }}>No active stream</span>
        </div>
      )}

      {/* Connecting spinner (BUG-09: show descriptive text) */}
      {connectionState === "connecting" && !videoTimedOut && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.6)",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: "3px solid #333",
              borderTopColor: "#2563eb",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <span style={{ color: "#9ca3af", fontSize: 13 }}>
            Connecting to video stream...
          </span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Connected but no video frames yet (BUG-05: timeout, BUG-06: progress) */}
      {connectionState === "connected" && isWaiting && !videoTimedOut && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            color: "#9ca3af",
            fontSize: 14,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              border: "3px solid #333",
              borderTopColor: "#2563eb",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          Waiting for video stream...
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            {waitingElapsed}s elapsed
          </span>
        </div>
      )}

      {/* v1.9.3: the VM is away and the backend keeps the page attached; the
          stream resumes by itself when it is back. */}
      {vmWaiting && !transportInterrupted && (
        <div
          role="status"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            color: "#e5e7eb",
            fontSize: 14,
          }}
        >
          <span>La machine virtuelle redémarre</span>
          <span style={{ fontSize: 12, color: "#9ca3af" }}>
            La vidéo reprendra d’elle-même dès son retour.
          </span>
        </div>
      )}

      {/* A retained track/thumbnail must not hide a failed video transport. */}
      {!vmWaiting && ((isWaiting && videoTimedOut) || transportInterrupted) && (
        <div
          role="alert"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            color: "#f87171",
            fontSize: 14,
          }}
        >
          <span>
            {transportInterrupted
              ? "Connexion vidéo interrompue"
              : connectionState === "connecting"
                ? `Connexion vidéo non établie après ${videoTimeoutSec} s`
                : `Aucune image reçue après ${videoTimeoutSec} s`}
          </span>
          <span style={{ fontSize: 12, color: "#9ca3af" }}>
            {/* v1.9.5 (B1) : la page relance seule la vidéo après une coupure. */}
            {transportInterrupted
              ? "Reconnexion automatique en cours. Le navigateur peut encore fonctionner."
              : "Le flux vidéo n’est pas disponible. Le navigateur peut encore fonctionner."}
          </span>
        </div>
      )}

      {/* Resolution change overlay (BUG-13) */}
      {resolutionChanging && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.7)",
            color: "#e5e7eb",
            fontSize: 15,
            zIndex: 10,
          }}
        >
          Changing resolution...
        </div>
      )}

      {/* Pointer lock hint (BUG-02) */}
      {pointerLockEnabled && connectionState === "connected" && videoReady && !document.pointerLockElement && (
        <div
          style={{
            position: "absolute",
            bottom: 12,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0, 0, 0, 0.75)",
            color: "#e5e7eb",
            padding: "6px 14px",
            borderRadius: 6,
            fontSize: 12,
            pointerEvents: "none",
            zIndex: 5,
          }}
        >
          Click to capture mouse — ESC to release
        </div>
      )}

      {/* "Ready" as soon as frames are decoded: the one-shot app_ready
          signal only reaches a page connected when the VM booted. */}
      <StatusOverlay
        connectionState={connectionState}
        stats={stats}
        visible={showStats}
        appReady={videoReady}
      />

    </div>
  );
}
